import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@promptwatch/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@promptwatch/ui/components/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@promptwatch/ui/components/form";
import { Input } from "@promptwatch/ui/components/input";
import { Textarea } from "@promptwatch/ui/components/textarea";
import { Building2, Mail, MapPin } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import BaseLayout from "@/components/base-layout";
import BrandCloud from "@/components/brand-cloud";
import { Header } from "@/components/header";
import FAQSection from "@/components/sections/faq";
import { trpc } from "@/utils/trpc";

const contactFormSchema = z.object({
  firstName: z.string().min(1, { message: "First name is required" }),
  lastName: z.string().min(1, { message: "Last name is required" }),
  email: z.string().email({ message: "Invalid email address" }),
  phone: z.string().optional(),
  company: z.string().optional(),
  message: z.string().min(10, { message: "Message must be at least 10 characters" }),
});

type ContactFormData = z.infer<typeof contactFormSchema>;

const ContactPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const contactMutation = trpc.contact.submitContactForm.useMutation();

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      company: "",
      message: "",
    },
  });

  const onSubmit = async (values: ContactFormData) => {
    setIsSubmitting(true);

    try {
      await contactMutation.mutateAsync(values);
      toast.success("Thank you for contacting us! We'll get back to you soon.");
      form.reset();
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error("There was an error sending your message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BaseLayout>
      <Header
        canonical="https://promptwatch.com/contact-us"
        title="Contact Us | Promptwatch.com"
        description="Get in touch with Promptwatch to learn how to boost your brand visibility in LLMs"
      />
      <main className="overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-200/50 via-blue-100/50" />
        <div
          aria-hidden="true"
          className="absolute h-full w-full scale-[0.8] bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] [mask-image:radial-gradient(ellipse_50%_50%_at_50%_50%,#fff_70%,transparent_100%)]"
        />

        {/* Breadcrumb Navigation */}
        <section className="relative border-b border-gray-950/[0.07]">
          <div className="relative mx-auto max-w-6xl border-x border-gray-950/[0.07] px-4 sm:px-6 md:px-12">
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -left-1 size-2 border shadow-sm z-40" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 left-3 size-2 border shadow-sm sm:left-5 md:left-11 z-40" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -right-1 size-2 border shadow-sm z-40" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 right-3 size-2 border shadow-sm sm:right-5 md:right-11 z-40" />
            <div className="relative border-x border-gray-950/[0.07] px-4 py-4 sm:px-12">
              <nav className="flex items-center space-x-2 text-sm text-gray-600">
                <Link href="/" className="hover:text-primary">
                  Home
                </Link>
                <span>/</span>
                <span className="text-gray-900 font-medium">Contact Us</span>
              </nav>
            </div>
          </div>
        </section>

        {/* Header Section */}
        <section className="relative border-b border-gray-950/[0.07]">
          <div className="relative mx-auto max-w-6xl border-x border-gray-950/[0.07] px-4 sm:px-6 md:px-12">
            <div aria-hidden="true" className="bg-ui absolute -top-1 -left-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -top-1 left-3 size-2 border shadow-sm sm:left-5 md:left-11" />
            <div aria-hidden="true" className="bg-ui absolute -top-1 -right-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -top-1 right-3 size-2 border shadow-sm sm:right-5 md:right-11" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -left-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 left-3 size-2 border shadow-sm sm:left-5 md:left-11" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -right-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 right-3 size-2 border shadow-sm sm:right-5 md:right-11" />
            <div className="relative border-x border-gray-950/[0.07] px-4 py-8 sm:px-12">
              <div className="pt-6 mb-4">
                <h1 className="text-title text-3xl font-medium text-gray-900 sm:text-4xl max-w-4xl">Contact</h1>
              </div>
              <div className="pb-5 max-w-2xl">
                <p className="text-muted-foreground text-lg leading-relaxed">Get in touch with our team.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Content */}
        <section className="relative border-b border-gray-950/[0.07]">
          <div className="relative mx-auto max-w-6xl border-x border-gray-950/[0.07] px-4 sm:px-6 md:px-12">
            <div aria-hidden="true" className="bg-ui absolute -top-1 -left-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -top-1 left-3 size-2 border shadow-sm sm:left-5 md:left-11" />
            <div aria-hidden="true" className="bg-ui absolute -top-1 -right-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -top-1 right-3 size-2 border shadow-sm sm:right-5 md:right-11" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -left-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 left-3 size-2 border shadow-sm sm:left-5 md:left-11" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -right-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 right-3 size-2 border shadow-sm sm:right-5 md:right-11" />
            <div className="bg-white/50 relative border-x border-gray-950/[0.07] px-4 py-12 sm:px-12">
              <div className="grid lg:grid-cols-2 gap-12">
                {/* Location Details */}
                <div className="space-y-8">
                  <div>
                    <h3 className="text-xl font-semibold mb-6">Get in Touch</h3>
                    <div className="space-y-4">
                      <div className="flex items-start gap-4">
                        <MapPin className="w-5 h-5 text-primary mt-1" />
                        <div>
                          <p className="font-medium">Office Address</p>
                          <p className="text-muted-foreground">
                            Vijzelstraat 68
                            <br />
                            1017 HL Amsterdam
                            <br />
                            Netherlands
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-4">
                        <Mail className="w-5 h-5 text-primary mt-1" />
                        <div>
                          <p className="font-medium">Email</p>
                          <a
                            href="mailto:team@promptwatch.com"
                            className="text-muted-foreground underline hover:text-primary transition-colors"
                          >
                            team@promptwatch.com
                          </a>
                        </div>
                      </div>

                      <div className="flex items-start gap-4">
                        <Building2 className="w-5 h-5 text-primary mt-1" />
                        <div>
                          <p className="font-medium">Business Hours</p>
                          <p className="text-muted-foreground">Monday - Friday: 8:00 AM - 6:00 PM CET</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h4 className="font-semibold mb-2">Quick Response Time</h4>
                    <p className="text-sm text-muted-foreground">We typically respond to inquiries within 24 business hours.</p>
                  </div>
                </div>

                {/* Contact Form */}
                <div className="mt-12 lg:mt-0">
                  <Card>
                    <CardHeader>
                      <CardTitle>Send us a Message</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <FormField
                              control={form.control}
                              name="firstName"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>First Name *</FormLabel>
                                  <FormControl>
                                    <Input placeholder="First name" {...field} disabled={isSubmitting} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name="lastName"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Last Name *</FormLabel>
                                  <FormControl>
                                    <Input placeholder="Last name" {...field} disabled={isSubmitting} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>

                          <FormField
                            control={form.control}
                            name="email"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Email *</FormLabel>
                                <FormControl>
                                  <Input placeholder="firstname@promptwatch.com" type="email" {...field} disabled={isSubmitting} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="phone"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Phone Number (Optional)</FormLabel>
                                <FormControl>
                                  <Input placeholder="+1 (555) 123-4567" type="tel" {...field} disabled={isSubmitting} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="company"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Company (Optional)</FormLabel>
                                <FormControl>
                                  <Input placeholder="Company Name" {...field} disabled={isSubmitting} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="message"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Message *</FormLabel>
                                <FormControl>
                                  <Textarea
                                    placeholder="Tell us how we can help you..."
                                    className="min-h-[80px]"
                                    {...field}
                                    disabled={isSubmitting}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <Button type="submit" className="w-full" disabled={isSubmitting}>
                            {isSubmitting ? "Sending..." : "Send Message"}
                          </Button>
                        </form>
                      </Form>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b">
          <div className="mx-auto max-w-6xl border-x px-3 py-10">
            <div className="p-10 px-0 z-10 relative">
              <h3 className="text-body text-center text-sm font-medium">Promptwatch is trusted by 500+ Companies</h3>
              <BrandCloud />
            </div>
          </div>
        </section>

        <section className="relative border-b overflow-hidden border-gray-950/[0.07]">
          <div className="relative mx-auto max-w-6xl border-x border-gray-950/[0.07] px-4 sm:px-6 md:px-12">
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -left-1 size-2 border shadow-sm z-40" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 left-3 size-2 border shadow-sm sm:left-5 md:left-11 z-40" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -right-1 size-2 border shadow-sm z-40" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 right-3 size-2 border shadow-sm sm:right-5 md:right-11 z-40" />
            <div className="relative border-x border-gray-950/[0.07] px-4 pb-16 pt-16 sm:px-12">
              <FAQSection />
            </div>
          </div>
        </section>
      </main>
    </BaseLayout>
  );
};

export default ContactPage;
